import { cn } from '@foundation/utils/cn';
import { Close } from '@design-system/icons';

import {
  getResponsiveEntries,
  HeadlessAppFrame,
  resolveResponsiveProp,
  useAppFrame,
  type AppFramePanelWidth,
  type Breakpoint,
  type HeadlessAppFrameContentProps,
  type HeadlessAppFramePanelProps,
  type HeadlessAppFramePanelHeaderProps,
  type HeadlessAppFramePanelBodyProps,
  type HeadlessAppFrameRootProps,
  type HeadlessAppFrameTriggerProps,
  type HeadlessDrawerStyles,
} from '@design-system/headless-ui';

import { IconButton } from './IconButton';

// Literal classes only - Tailwind cannot generate these from a runtime value.
// The px must match APP_FRAME_PANEL_WIDTH_PX in headless-ui/components/AppFrame.
const PANEL_WIDTHS = {
  base: { small: 'w-[320px]', medium: 'w-[400px]', large: 'w-[560px]' },
  sm: { small: 'sm:w-[320px]', medium: 'sm:w-[400px]', large: 'sm:w-[560px]' },
  md: { small: 'md:w-[320px]', medium: 'md:w-[400px]', large: 'md:w-[560px]' },
  lg: { small: 'lg:w-[320px]', medium: 'lg:w-[400px]', large: 'lg:w-[560px]' },
  xl: { small: 'xl:w-[320px]', medium: 'xl:w-[400px]', large: 'xl:w-[560px]' },
} as const satisfies Record<Breakpoint, Record<AppFramePanelWidth, string>>;

const PUSH_WIDTHS = {
  base: {
    small: 'data-active:mr-[320px]',
    medium: 'data-active:mr-[400px]',
    large: 'data-active:mr-[560px]',
  },
  sm: {
    small: 'sm:data-active:mr-[320px]',
    medium: 'sm:data-active:mr-[400px]',
    large: 'sm:data-active:mr-[560px]',
  },
  md: {
    small: 'md:data-active:mr-[320px]',
    medium: 'md:data-active:mr-[400px]',
    large: 'md:data-active:mr-[560px]',
  },
  lg: {
    small: 'lg:data-active:mr-[320px]',
    medium: 'lg:data-active:mr-[400px]',
    large: 'lg:data-active:mr-[560px]',
  },
  xl: {
    small: 'xl:data-active:mr-[320px]',
    medium: 'xl:data-active:mr-[400px]',
    large: 'xl:data-active:mr-[560px]',
  },
} as const satisfies Record<Breakpoint, Record<AppFramePanelWidth, string>>;

export type AppFrameRootProps = Omit<HeadlessAppFrameRootProps, 'className'>;
export type AppFrameContentProps = Omit<
  HeadlessAppFrameContentProps,
  'className'
>;
export type AppFramePanelProps = Omit<HeadlessAppFramePanelProps, 'styles'>;
export type AppFramePanelHeaderProps = Omit<
  HeadlessAppFramePanelHeaderProps,
  'className' | 'closeButton'
>;
export type AppFramePanelBodyProps = Omit<
  HeadlessAppFramePanelBodyProps,
  'className'
>;
export type AppFrameTriggerProps = HeadlessAppFrameTriggerProps;

function Root(props: AppFrameRootProps) {
  return <HeadlessAppFrame.Root {...props} />;
}

function Content({ children, ...rest }: AppFrameContentProps) {
  const { presentation, width } = useAppFrame();
  const widthEntries = getResponsiveEntries(
    resolveResponsiveProp(width, 'medium'),
  );

  return (
    <HeadlessAppFrame.Content
      {...rest}
      className={
        presentation === 'side'
          ? cn(
              'mr-0',
              widthEntries.map(([bp, size]) => PUSH_WIDTHS[bp][size]),
              'transition-[margin] duration-300 ease-out motion-reduce:transition-none',
            )
          : undefined
      }
    >
      {children}
    </HeadlessAppFrame.Content>
  );
}

const PANEL_SURFACE =
  'bg-pmui-bg-primary text-pmui-fg-primary rounded-none shadow-none';

function Panel({
  children,
  'data-testid': dataTestId,
  ...rest
}: AppFramePanelProps) {
  const { presentation, width } = useAppFrame();
  const isSide = presentation === 'side';
  const widthEntries = getResponsiveEntries(
    resolveResponsiveProp(width, 'medium'),
  );

  const drawerStyles: HeadlessDrawerStyles = {
    // The viewport spans the screen; only the panel itself may take pointers,
    // otherwise it would swallow clicks on the content it pushes.
    container: cn(
      'pointer-events-none items-stretch',
      isSide ? 'justify-end' : 'inset-x-0 bottom-0 justify-center',
    ),
    panel: cn(
      PANEL_SURFACE,
      'h-full',
      isSide
        ? cn(
            widthEntries.map(([bp, size]) => PANEL_WIDTHS[bp][size]),
            'border-l border-pmui-border-primary',
          )
        : 'w-full',
    ),
    swipeHandle:
      'mt-2 h-1 w-12 rounded-full bg-pmui-interactive-bg-default-pressed',
  };

  return (
    <HeadlessAppFrame.Panel
      {...rest}
      styles={drawerStyles}
      data-testid={dataTestId}
    >
      {children}
    </HeadlessAppFrame.Panel>
  );
}

function PanelHeader({
  children,
  'data-testid': dataTestId,
}: AppFramePanelHeaderProps) {
  return (
    <HeadlessAppFrame.PanelHeader
      closeButton={
        <IconButton
          aria-label="Close"
          type="button"
          icon={<Close size={24} />}
          size="small"
          variant="ghost"
          data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}
        />
      }
      className="gap-1 px-2 py-1 min-h-12 [&>div]:pl-2 [--app-frame-header-padding-top:0.25rem]"
      data-testid={dataTestId}
    >
      {children}
    </HeadlessAppFrame.PanelHeader>
  );
}

function PanelBody(props: AppFramePanelBodyProps) {
  return <HeadlessAppFrame.PanelBody {...props} />;
}

export const AppFrame = {
  Root,
  Content,
  Panel,
  PanelHeader,
  PanelBody,
  Trigger: HeadlessAppFrame.Trigger,
};

export { useAppFrame } from '@design-system/headless-ui';
